import type { MemoExoticComponent, JSX } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { InlineCommentSelectionComponentProps, InlineCommentViewComponentProps, InlineCommentHoverComponentProps } from '@atlaskit/editor-common/types/annotation';
import type { AnnotationTypes, AnnotationMarkStates } from '@atlaskit/adf-schema/annotation';
import type { AnnotationState } from '@atlaskit/editor-plugins/annotation';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { getRendererAnnotationStates } from '@confluence/comments-util/entry-points/stateResolver.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { HoverComponentProps } from './HoverComponent.tsx';
import type { ViewComponentProps } from './ViewComponent.tsx';
import { SelectionManager, type BaseSelectionComponentProps } from './SelectionManager.tsx';
import { ViewManager } from './ViewManager.tsx';
import { HoverManager } from './HoverManager.tsx';

type getRendererInlineCommentOptionsProps = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	setSelectionProps: (
		selectionProps: (InlineCommentSelectionComponentProps & BaseSelectionComponentProps) | null,
	) => void;
	setHoverProps: (
		hoverProps: (InlineCommentHoverComponentProps & HoverComponentProps) | null,
	) => void;
	setViewProps: (viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null) => void;
	updateDocument?: (doc: JSONDocNode) => void;
	isArchived?: boolean;
	allowCommentsOnMedia?: boolean;
	isHighlightOptionPanelEnabled: boolean;
	/**
	 * Returns the latest merged reaction marker refs at call time. Using a getter (rather than
	 * a plain array) ensures the `getState` closure always reads the current value even when
	 * the renderer calls an old closure before it receives the new `annotationProvider` prop —
	 * preventing the stale-closure race that caused resolved highlights to reappear (CCCEMAU-2679).
	 */
	getReactionMarkerRefs?: () => string[];
	/** Returns the latest merged reaction marker map for the inline reaction view component. */
	getReactionMarkerMap?: () => Record<string, string>;
	/** Returns marker refs that should be treated as resolved even if comment state cache is stale. */
	getOptimisticResolvedMarkerRefs?: () => string[];
};

export const getRendererInlineCommentOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	setSelectionProps,
	setViewProps,
	updateDocument,
	isArchived,
	setHoverProps,
	isHighlightOptionPanelEnabled,
	getReactionMarkerRefs,
	getReactionMarkerMap,
	getOptimisticResolvedMarkerRefs,
}: getRendererInlineCommentOptionsProps): {
	selectionComponent:
		| MemoExoticComponent<(props: InlineCommentSelectionComponentProps) => null>
		| undefined;
	viewComponent: MemoExoticComponent<
		(viewComponentProps: InlineCommentViewComponentProps) => JSX.Element
	>;
	hoverComponent: MemoExoticComponent<(props: InlineCommentHoverComponentProps) => null>;
	getState: (
		annotations: string[],
		isNestedRender: boolean,
	) => Promise<AnnotationState<AnnotationTypes, AnnotationMarkStates>[]>;
	updateSubscriber: any;
	disallowOnWhitespace: boolean;
	isToolbarAbove: boolean;
	allowDraftMode: boolean;
	allowCommentsOnMedia: boolean;
} => {
	let updateSubscriber = getRendererAnnotationEventEmitter();
	if (!updateSubscriber?.off) {
		updateSubscriber = undefined;
	}

	return {
		selectionComponent:
			isHighlightOptionPanelEnabled ||
			expValEquals('confluence_frontend_allow_comments_if_toolbar_off', 'isEnabled', true)
				? SelectionManager({
						pageId,
						updateDocument,
						isArchived,
						setSelectionProps,
						onCommentButtonMount: () => CommentEditor.preload(),
					})
				: undefined,
		viewComponent: ViewManager({
			pageId,
			updateDocument,
			isArchived,
			setViewProps,
			getReactionMarkerMap,
		}),
		hoverComponent: HoverManager({
			pageId,
			updateDocument,
			isArchived,
			setHoverProps,
			onCommentButtonMount: () => CommentEditor.preload(),
		}),
		getState: (
			annotations: string[],
			isNestedRender: boolean,
		): Promise<AnnotationState<AnnotationTypes, AnnotationMarkStates>[]> => {
			const reactionMarkerRefs = getReactionMarkerRefs?.();
			const optimisticResolvedMarkerRefs = getOptimisticResolvedMarkerRefs?.();

			return getRendererAnnotationStates({
				annotations,
				pageId,
				validAnnotationsCallback,
				triggerAnalyticsEvent,
				isNestedRender,
				// Always call the getter at execution time so resolved highlights don't reappear
				// when the renderer calls a stale getState closure (CCCEMAU-2679).
				reactionMarkerRefs,
				optimisticResolvedMarkerRefs,
			});
		},
		updateSubscriber,
		disallowOnWhitespace: true,
		isToolbarAbove: true,
		allowDraftMode: true,
		allowCommentsOnMedia: true,
	};
};
